import { Suspense, lazy, useEffect, useRef } from "react";
import { Route, Switch } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { queryClient } from "@/lib/queryClient";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { useIdleTimer } from "@/hooks/use-idle-timer";
import { useLockPrefs } from "@/hooks/use-lock-prefs";
import { useSession } from "@/hooks/use-session";
import { DesktopLayout } from "@/components/DesktopLayout";
import { AuthGuard } from "@/components/AuthGuard";
import { AdminGuard } from "@/components/AdminGuard";
import { LockOverlay } from "@/components/LockOverlay";
import { MobileLockOverlay } from "@/mobile/components/MobileLockOverlay";
import { useIdentify } from "@/hooks/use-identify";
import { useLocked, wireLockEvents, setLocked as setLockStore } from "@/lib/lockStatus";

const MobileApp = lazy(() =>
  import("@/mobile/MobileApp").then((m) => ({ default: m.MobileApp })),
);
const Login = lazy(() => import("@/pages/Login"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const UnifiedHome = lazy(() => import("@/pages/UnifiedHome"));
const Settings = lazy(() => import("@/pages/Settings"));
const AuditLog = lazy(() => import("@/pages/AuditLog"));
const Users = lazy(() => import("@/pages/Users"));
const InviteUser = lazy(() => import("@/pages/InviteUser"));
const TemplateAdmin = lazy(() => import("@/pages/TemplateAdmin"));
const PromissoryNotes = lazy(() => import("@/pages/PromissoryNotes"));
const Properties = lazy(() => import("@/pages/Properties"));
const PropertyCreate = lazy(() => import("@/pages/PropertyCreate"));
const PropertyDetail = lazy(() => import("@/pages/PropertyDetail"));
const Documents = lazy(() => import("@/pages/Documents"));
const DrawRequests = lazy(() => import("@/pages/DrawRequests"));
const NotFound = lazy(() => import("@/pages/not-found"));

const loader = (
  <div className="flex min-h-screen items-center justify-center">
    <p className="text-muted-foreground">Loading…</p>
  </div>
);

function DesktopApp() {
  return (
    <Suspense fallback={loader}>
      <Switch>
        <Route path="/login">
          <Login />
        </Route>
        <Route path="/accept-invite">
          <AcceptInvite />
        </Route>
        <Route path="/forgot-password">
          <ForgotPassword />
        </Route>
        <Route path="/reset-password">
          <ResetPassword />
        </Route>
        <Route path="/">
          <AuthGuard>
            <DesktopLayout>
              <UnifiedHome />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route path="/settings">
          <AuthGuard>
            <DesktopLayout>
              <Settings />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route path="/settings/audit-log">
          <AuthGuard>
            <AdminGuard>
              <DesktopLayout>
                <AuditLog />
              </DesktopLayout>
            </AdminGuard>
          </AuthGuard>
        </Route>
        <Route path="/users">
          <AuthGuard>
            <AdminGuard>
              <DesktopLayout>
                <Users />
              </DesktopLayout>
            </AdminGuard>
          </AuthGuard>
        </Route>
        <Route path="/users/invite">
          <AuthGuard>
            <AdminGuard>
              <DesktopLayout>
                <InviteUser />
              </DesktopLayout>
            </AdminGuard>
          </AuthGuard>
        </Route>
        <Route path="/admin/templates">
          <AuthGuard>
            <AdminGuard>
              <DesktopLayout>
                <TemplateAdmin />
              </DesktopLayout>
            </AdminGuard>
          </AuthGuard>
        </Route>
        <Route path="/admin/promissory-notes">
          <AuthGuard>
            <AdminGuard>
              <DesktopLayout>
                <PromissoryNotes />
              </DesktopLayout>
            </AdminGuard>
          </AuthGuard>
        </Route>
        <Route path="/properties">
          <AuthGuard>
            <DesktopLayout>
              <Properties />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route path="/properties/new">
          <AuthGuard>
            <DesktopLayout>
              <PropertyCreate />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route path="/properties/:id">
          <AuthGuard>
            <DesktopLayout>
              <PropertyDetail />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route path="/documents">
          <AuthGuard>
            <DesktopLayout>
              <Documents />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route path="/draw-requests">
          <AuthGuard>
            <DesktopLayout>
              <DrawRequests />
            </DesktopLayout>
          </AuthGuard>
        </Route>
        <Route>
          <NotFound />
        </Route>
      </Switch>
    </Suspense>
  );
}

function IdentifyEffect() {
  useIdentify();
  return null;
}

// Wire the lock-state event listeners once, as a side effect of
// rendering the App. Idempotent via a module-level guard.
wireLockEvents();

// When the server tells us the session is gone (any 401 without a
// LOCKED code), wipe the cached /api/me so AuthGuard sees data=null
// and routes the user to /login. Has to live where the queryClient
// is in scope.
if (typeof window !== "undefined") {
  window.addEventListener("cerebellum:auth-expired", () => {
    queryClient.setQueryData(["/api/me"], null);
  });
}

// Service-worker update detection. vite-plugin-pwa is configured with
// `skipWaiting: true` + `clientsClaim: true`, so when a new SW
// installs after a deploy it immediately takes control of any open
// tab. The browser fires `controllerchange` on the navigator's SW
// container at that moment — we use that as the cue to surface a
// non-blocking "new version available" toast. Without this nudge,
// users who keep the PWA open in the foreground sit on the previous
// JS bundle until they happen to navigate.
function ServiceWorkerUpdater() {
  const shownRef = useRef(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;
    const onChange = () => {
      if (shownRef.current) return;
      shownRef.current = true;
      // Lazy import sonner to avoid pulling toast into the very first
      // render path.
      import("sonner").then(({ toast }) => {
        toast("A new version of Cerebellum is available.", {
          duration: Infinity,
          action: {
            label: "Reload",
            onClick: () => window.location.reload(),
          },
        });
      });
    };
    navigator.serviceWorker.addEventListener("controllerchange", onChange);
    return () => navigator.serviceWorker.removeEventListener("controllerchange", onChange);
  }, []);
  return null;
}

/**
 * Session-gated lock controller — renders nothing when the user isn't
 * authenticated so the idle timer doesn't fire on /login etc. Inside here
 * useSession has the QueryClient context it needs.
 */
function SessionLockController({ isMobile }: { isMobile: boolean }) {
  const { data: user, refetch: refetchSession } = useSession();
  const authed = Boolean(user);
  const { prefs } = useLockPrefs();
  const { idle, reset } = useIdleTimer(
    prefs.timeoutMinutes * 60 * 1000,
    prefs.enabled,
  );
  const locked = useLocked();
  const prevAuthedRef = useRef(authed);

  // When authed flips false → true (fresh login), clear any idle
  // state that accumulated on the login screen. Otherwise the
  // idle=true signal carries over and the lock fires on first render
  // of the authenticated app.
  //
  // When authed flips true → false (server-side session expiry, sign
  // out, etc.), clear the lock state too. Otherwise a stale
  // `locked === true` keeps the overlay sitting on top of the login
  // screen after auto-logout.
  useEffect(() => {
    if (!prevAuthedRef.current && authed) {
      reset();
      setLockStore(false);
    } else if (prevAuthedRef.current && !authed) {
      setLockStore(false);
    }
    prevAuthedRef.current = authed;
  }, [authed, reset]);

  useEffect(() => {
    if (!authed) return;
    if (idle) {
      fetch("/api/session/lock", { method: "POST", credentials: "include" }).catch(() => {});
      setLockStore(true);
    }
  }, [idle, authed]);

  // Belt-and-suspenders: if we somehow end up locked while not
  // authenticated, hide the overlay. The login screen owns the UI.
  if (!locked || !authed) return null;
  const onUnlock = () => {
    setLockStore(false);
    reset();
    // Re-fetch /api/me so the rest of the app gets the unlocked user
    // data without requiring another round trip.
    refetchSession();
  };
  return isMobile ? (
    <MobileLockOverlay onUnlock={onUnlock} />
  ) : (
    <LockOverlay onUnlock={onUnlock} />
  );
}

export function App() {
  const isMobile = useIsMobile();
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <IdentifyEffect />
          <ServiceWorkerUpdater />
          {isMobile ? <MobileApp /> : <DesktopApp />}
          <SessionLockController isMobile={isMobile} />
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
